// Settings service with API contracts matching backend schemas
import { PAGINATION } from '@/config/constants'
import { billingApi } from './billingApi'
import { API_BASE, APIError, getAccessToken } from './api'

export interface OrgSettings {
  from_name: string;
  from_email: string;
  company_name: string;
  website?: string;
  brand_voice?: string;
  brand_voice_settings?: BrandVoiceSettings;
  onboarding_completed?: boolean;
  onboarding_completed_at?: string;
  onboarding_skipped?: boolean;
  customer_goal?: 'find_buyers' | 'linkedin_presence' | 'market_research';
}

export interface BrandVoiceSettings {
  tone: string;
  brand_voice: string;
  do: string[];
  dont: string[];
  proof_points: string[];
  custom_instructions: string;
  website_summaries: string[];
  version: string;
  company_name?: string;
  website?: string;
}

export interface IntegrationSettings {
  hubspot?: {
    enabled: boolean;
    api_key?: string;
    sync_enabled: boolean;
  };
  salesforce?: {
    enabled: boolean;
    client_id?: string;
    client_secret?: string;
    sync_enabled: boolean;
  };
  linkedin?: {
    enabled: boolean;
    api_key?: string;
    rate_limit: number;
  };
  linkedin_outreach?: {
    enabled: boolean;
    worker_url?: string;
    worker_api_key?: string;
  };
  slack?: {
    enabled: boolean;
    team_id?: string;
    team_name?: string;
    bot_user_id?: string;
    authed_user_id?: string;
    scope?: string;
    installed_at?: string;
  };
}

export interface NotificationSettings {
  email_notifications: boolean;
  email_on_reply?: boolean;
  email_on_bounce?: boolean;
  email_on_open?: boolean;
  email_digest?: string;
  slack_webhook?: string;
  slack_enabled?: boolean;
  push_enabled?: boolean;
  daily_summary: boolean;
  weekly_report: boolean;
}

export interface UserRole {
  id: string;
  name: string;
  permissions: string[];
}

export interface TeamMember {
  id: string;
  email: string;
  name: string;
  // Backend (team_management_service) serialises role as the UserRole enum VALUE — a
  // string like "admin" / "sales_manager" / "sales_rep" — not a {id,name} object.
  role: string;
  status: 'active' | 'pending' | 'suspended';
  lastActive?: string;
}

// Billing types — re-exported from billingApi (single source of truth)
export type {
  BillingPlan,
  CurrentSubscription,
  UsageMetrics,
  Invoice,
  PaymentMethod,
} from './billingApi'

// Economic Additionality Consent Settings
export type ConsentType = 'share_playbooks' | 'share_aggregated_signals' | 'contribute_to_network';

export interface ConsentSettings {
  share_playbooks: boolean;
  share_aggregated_signals: boolean;
  contribute_to_network: boolean;
}

export interface ConsentHistoryEntry {
  id: string;
  tenant_id: string;
  consent_type: ConsentType;
  consented: boolean;
  updated_by: string | null;
  updated_at: string;
  ip_address?: string;
}

// LLM Settings Types
export interface LLMModel {
  id: string;
  provider: string;
  model_id: string;
  display_name: string;
  description?: string;
  supports_chat: boolean;
  supports_function_calling: boolean;
  supports_vision: boolean;
  max_context_tokens?: number;
}

export interface CustomProviderInfo {
  configured: boolean;
  base_url?: string;
}

export interface LLMConfig {
  default_chat_model_id?: string;
  default_chat_model_name?: string;
  default_embedding_model_id?: string;
  allowlist_mode: string;
  monthly_budget_usd?: number;
  monthly_usage_usd: number;
  budget_remaining_usd?: number;
  max_requests_per_minute?: number;
  max_tokens_per_request?: number;
  has_custom_openai_key: boolean;
  has_custom_anthropic_key: boolean;
  custom_ollama_endpoint?: string;
  custom_providers: Record<string, CustomProviderInfo>;
  security_profile: string;
  plan_tier: string;
  catalog_unlocked: boolean;
}

export interface UpdateLLMConfigRequest {
  default_chat_model_id?: string;
  monthly_budget_usd?: number;
  max_requests_per_minute?: number;
  max_tokens_per_request?: number;
}

export interface LLMUsage {
  monthly_usage_usd: number;
  monthly_budget_usd?: number;
  budget_remaining_usd?: number;
  is_budget_exceeded: boolean;
  budget_period_start?: string;
}

export interface LLMTestResult {
  status: string;
  model_id: string;
  provider: string;
  message: string;
}

// Team Management Types
export interface PendingInvite {
  id: string;
  email: string;
  role: string;
  status: string;
  expires_at: string;
  created_at: string;
}

export interface TeamMembersResponse {
  members: TeamMember[];
  pending_invites: PendingInvite[];
  total_members: number;
  total_pending: number;
}

export interface TeamInviteResponse {
  success: boolean;
  invite_id: string;
  email: string;
  role: string;
  expires_at: string;
  message: string;
}

// Agent Autonomy Settings
export type AgentAutonomyMode = 'full_auto' | 'supervised' | 'manual';
export type BuyerExecutionApprovalLevel = 'always' | 'high_risk' | 'policy_only';

export interface QuietHours {
  start_hour: number; // UTC 0-23
  end_hour: number;   // UTC 0-23
}

export interface AgentAutonomySettings {
  mode: AgentAutonomyMode;
  approval_level: BuyerExecutionApprovalLevel;
  // Proactive scheduler (#503/#514). proactive_enabled = surface proactively;
  // auto_send_enabled = act WITHOUT approval (gated by contact:auto_send).
  proactive_enabled: boolean;
  auto_send_enabled: boolean;
  cadence_hours: number;
  max_commits_per_sweep: number;
  enabled_sources: string[];
  quiet_hours: QuietHours;
}

class SettingsService {
  private baseUrl = `${API_BASE}/settings`;

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    const token = getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  async getOrgSettings(): Promise<OrgSettings> {
    const response = await fetch(`${this.baseUrl}/org`, {
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch org settings');
    return response.json();
  }

  async saveOrgSettings(settings: Partial<OrgSettings>): Promise<{ success: boolean }> {
    const response = await fetch(`${this.baseUrl}/org`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(settings)
    });
    if (!response.ok) throw new Error('Failed to save org settings');
    return response.json();
  }

  async getIntegrationSettings(): Promise<IntegrationSettings> {
    const response = await fetch(`${this.baseUrl}/integrations`, {
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch integration settings');
    return response.json();
  }

  async saveIntegrationSettings(settings: IntegrationSettings): Promise<{ success: boolean }> {
    const response = await fetch(`${this.baseUrl}/integrations`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(settings)
    });
    if (!response.ok) throw new Error('Failed to save integration settings');
    return response.json();
  }

  async getNotificationSettings(): Promise<NotificationSettings> {
    const response = await fetch(`${this.baseUrl}/notifications`, {
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch notification settings');
    return response.json();
  }

  async saveNotificationSettings(settings: NotificationSettings): Promise<{ success: boolean }> {
    const response = await fetch(`${this.baseUrl}/notifications`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(settings)
    });
    if (!response.ok) throw new Error('Failed to save notification settings');
    return response.json();
  }

  async getTeamMembers(): Promise<TeamMember[]> {
    const response = await fetch(`${this.baseUrl}/team`, {
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch team members');
    return response.json();
  }

  async inviteTeamMember(email: string, roleId: string): Promise<{ success: boolean }> {
    const response = await fetch(`${this.baseUrl}/team/invite`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ email, role_id: roleId })
    });
    if (!response.ok) throw new Error('Failed to invite team member');
    return response.json();
  }

  async updateTeamMemberRole(memberId: string, roleId: string): Promise<{ success: boolean }> {
    const response = await fetch(`${this.baseUrl}/team/${memberId}/role`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify({ role_id: roleId })
    });
    if (!response.ok) throw new Error('Failed to update team member role');
    return response.json();
  }

  async getRoles(): Promise<UserRole[]> {
    const response = await fetch(`${this.baseUrl}/roles`, {
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch roles');
    return response.json();
  }

  // Economic Additionality Consent Management
  async getConsentSettings(): Promise<ConsentSettings> {
    const response = await fetch(`${this.baseUrl}/consents`, {
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch consent settings');
    return response.json();
  }

  async updateConsent(
    consentType: ConsentType,
    consented: boolean
  ): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${this.baseUrl}/consents`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        consent_type: consentType,
        consented
      })
    });
    if (!response.ok) throw new Error('Failed to update consent');
    return response.json();
  }

  async getConsentHistory(
    consentType?: ConsentType,
    limit: number = PAGINATION.SETTINGS_PAGE_SIZE
  ): Promise<ConsentHistoryEntry[]> {
    const params = new URLSearchParams();
    if (consentType) params.append('consent_type', consentType);
    params.append('limit', limit.toString());

    const response = await fetch(`${this.baseUrl}/consents/history?${params.toString()}`, {
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch consent history');
    const data = await response.json();
    // Backend returns { items: [...], count, limit } wrapper
    return Array.isArray(data) ? data : (data.items ?? []);
  }

  async revokeAllConsents(): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${this.baseUrl}/consents/revoke-all`, {
      method: 'POST',
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Failed to revoke consents');
    return response.json();
  }

  // Billing & Subscription Management — delegates to billingApi (single source of truth)
  getCurrentSubscription = billingApi.getCurrentSubscription
  getUsageMetrics = billingApi.getUsageMetrics
  getAvailablePlans = billingApi.getAvailablePlans
  changePlan = billingApi.changePlan
  cancelSubscription = billingApi.cancelSubscription
  getInvoices = billingApi.getInvoices
  getPaymentMethods = billingApi.getPaymentMethods
  openBillingPortal = billingApi.openBillingPortal

  // Brand voice
  async analyzeBrandWebsite(url: string): Promise<{
    suggestedTone: string;
    characteristics: string[];
    companyName: string;
    industry: string;
  }> {
    const response = await fetch(`${this.baseUrl}/brand-voice/analyze`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ url })
    });
    if (!response.ok) throw new Error('Website analysis is not yet available. Please configure your brand voice manually.');
    return response.json();
  }

  async getBrandVoice(): Promise<BrandVoiceSettings> {
    const response = await fetch(`${this.baseUrl}/brand-voice`, {
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch brand voice settings');
    return response.json();
  }

  async saveBrandVoice(payload: Partial<BrandVoiceSettings>): Promise<BrandVoiceSettings> {
    const response = await fetch(`${this.baseUrl}/brand-voice`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error('Failed to save brand voice settings');
    return response.json();
  }

  // LLM Settings
  async getLLMConfig(): Promise<LLMConfig> {
    const response = await fetch(`${this.baseUrl}/llm/`, {
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch LLM config');
    return response.json();
  }

  async updateLLMConfig(config: UpdateLLMConfigRequest): Promise<LLMConfig> {
    const response = await fetch(`${this.baseUrl}/llm/`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(config)
    });
    if (!response.ok) throw new Error('Failed to update LLM config');
    return response.json();
  }

  async getAvailableLLMModels(): Promise<LLMModel[]> {
    const response = await fetch(`${this.baseUrl}/llm/available-models`, {
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch available models');
    return response.json();
  }

  async getLLMUsage(): Promise<LLMUsage> {
    const response = await fetch(`${this.baseUrl}/llm/usage`, {
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch LLM usage');
    return response.json();
  }

  async setCustomOllamaEndpoint(endpoint: string): Promise<{ status: string }> {
    const response = await fetch(`${this.baseUrl}/llm/custom-ollama`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify({ endpoint_url: endpoint })
    });
    if (!response.ok) throw new Error('Failed to set Ollama endpoint');
    return response.json();
  }

  async removeCustomOllamaEndpoint(): Promise<{ status: string }> {
    const response = await fetch(`${this.baseUrl}/llm/custom-ollama`, {
      method: 'DELETE',
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Failed to remove Ollama endpoint');
    return response.json();
  }

  async setCustomOpenAIKey(apiKey: string): Promise<{ status: string }> {
    const response = await fetch(`${this.baseUrl}/llm/custom-openai-key`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify({ api_key: apiKey })
    });
    if (!response.ok) throw new Error('Failed to set OpenAI key');
    return response.json();
  }

  async removeCustomOpenAIKey(): Promise<{ status: string }> {
    const response = await fetch(`${this.baseUrl}/llm/custom-openai-key`, {
      method: 'DELETE',
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Failed to remove OpenAI key');
    return response.json();
  }

  async setCustomAnthropicKey(apiKey: string): Promise<{ status: string }> {
    const response = await fetch(`${this.baseUrl}/llm/custom-anthropic-key`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify({ api_key: apiKey })
    });
    if (!response.ok) throw new Error('Failed to set Anthropic key');
    return response.json();
  }

  async removeCustomAnthropicKey(): Promise<{ status: string }> {
    const response = await fetch(`${this.baseUrl}/llm/custom-anthropic-key`, {
      method: 'DELETE',
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Failed to remove Anthropic key');
    return response.json();
  }

  async testLLMModel(modelId: string): Promise<LLMTestResult> {
    const response = await fetch(`${this.baseUrl}/llm/test-model`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ model_id: modelId })
    });
    if (!response.ok) throw new Error('Failed to test model');
    return response.json();
  }

  // Custom LLM Providers (DeepSeek, Mistral, Groq, etc.)
  async setCustomProvider(provider: string, apiKey: string, baseUrl?: string): Promise<{ status: string; provider: string; base_url?: string }> {
    const response = await fetch(`${this.baseUrl}/llm/custom-provider`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify({ provider, api_key: apiKey, base_url: baseUrl || undefined })
    });
    if (!response.ok) throw new Error('Failed to set custom provider');
    return response.json();
  }

  async removeCustomProvider(provider: string): Promise<{ status: string }> {
    const response = await fetch(`${this.baseUrl}/llm/custom-provider/${encodeURIComponent(provider)}`, {
      method: 'DELETE',
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Failed to remove custom provider');
    return response.json();
  }

  async listCustomProviders(): Promise<{ configured_providers: Record<string, CustomProviderInfo>; known_providers: Record<string, string> }> {
    const response = await fetch(`${this.baseUrl}/llm/custom-providers`, {
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Failed to list custom providers');
    return response.json();
  }

  // Agent Autonomy Settings
  async getAgentAutonomySettings(): Promise<AgentAutonomySettings> {
    const response = await fetch(`${this.baseUrl}/agent-autonomy`, {
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch agent autonomy settings');
    return response.json();
  }

  async saveAgentAutonomySettings(
    mode: AgentAutonomyMode,
    approvalLevel?: BuyerExecutionApprovalLevel,
    // Partial proactive-scheduler fields. The backend MERGES, so callers may
    // send only the fields they're changing (mode is always required).
    extra?: Partial<Omit<AgentAutonomySettings, 'mode' | 'approval_level'>>,
  ): Promise<AgentAutonomySettings> {
    const payload: Record<string, unknown> = { mode, ...(extra || {}) };
    if (approvalLevel) {
      payload.approval_level = approvalLevel;
    }
    const response = await fetch(`${this.baseUrl}/agent-autonomy`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      // Enabling autonomous send requires the contact:auto_send permission;
      // surface the 403 distinctly so the UI can explain it.
      if (response.status === 403) {
        throw new Error('You do not have permission to enable autonomous send (contact:auto_send).');
      }
      throw new Error('Failed to save agent autonomy settings');
    }
    return response.json();
  }


  // Team Management - Updated methods
  async getTeamMembersWithInvites(): Promise<TeamMembersResponse> {
    const response = await fetch(`${this.baseUrl}/team/members`, {
      headers: this.getHeaders()
    });
    if (!response.ok) {
      // Throw an APIError carrying the REAL status (so the caller can show a friendly,
      // status-appropriate message and we can tell a 401 from a 500) plus the server detail
      // for console/logs — the technical detail is for diagnosis, never shown raw to the
      // customer. Fixes the B3 "swallowed status" defect without leaking internals to the UI.
      let detail = '';
      try {
        const body = await response.json();
        detail = body?.detail ?? body?.message ?? '';
      } catch {
        /* non-JSON error body */
      }
      throw new APIError(
        response.status,
        response.statusText,
        detail,
        `Failed to fetch team members (HTTP ${response.status}${detail ? `: ${detail}` : ''})`
      );
    }
    return response.json();
  }

  async inviteTeamMemberNew(email: string, role: string): Promise<TeamInviteResponse> {
    const response = await fetch(`${this.baseUrl}/team/invite`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ email, role })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to invite team member');
    }
    return response.json();
  }

  async revokeTeamInvitation(inviteId: string): Promise<{ success: boolean }> {
    const response = await fetch(`${this.baseUrl}/team/invite/${inviteId}`, {
      method: 'DELETE',
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Failed to revoke invitation');
    return response.json();
  }

  async updateTeamMemberRoleNew(memberId: string, role: string): Promise<{ success: boolean }> {
    const response = await fetch(`${this.baseUrl}/team/members/${memberId}/role`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify({ role })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update role');
    }
    return response.json();
  }

  async removeTeamMember(memberId: string): Promise<{ success: boolean }> {
    const response = await fetch(`${this.baseUrl}/team/members/${memberId}`, {
      method: 'DELETE',
      headers: this.getHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to remove team member');
    }
    return response.json();
  }

  async getAvailableRoles(): Promise<{ roles: Array<{ id: string; name: string }> }> {
    const response = await fetch(`${this.baseUrl}/team/roles`, {
      headers: this.getHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch roles');
    return response.json();
  }
}

export const settingsService = new SettingsService();
